import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { useProjects } from "@/api/projects";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export function ProjectListPage(): JSX.Element {
  const { data: projects, isLoading, isError, error } = useProjects();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Projects" description="Loading…" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Projects" />
        <EmptyState
          title="Could not load projects."
          description={error instanceof Error ? error.message : "Unknown error."}
        />
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Projects" />
        <EmptyState
          title="No projects yet."
          description="Pilot tenants are seeded with one project by default. Contact your FlowStack administrator if you don't see one here."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Projects"
        description={
          projects.length === 1 ? "1 project." : `${projects.length} projects across this tenant.`
        }
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((project) => (
          <Link
            key={project.project_id}
            to={`/projects/${project.slug}`}
            className="group block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Card
              elevation="raised"
              className="h-full transition-colors group-hover:border-cyan/40 group-hover:bg-surface-2"
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="font-display text-lg">{project.display_name}</CardTitle>
                  <ArrowRight
                    className="h-4 w-4 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:text-cyan group-hover:opacity-100"
                    aria-hidden
                  />
                </div>
                <CardDescription className="font-mono text-xs">{project.slug}</CardDescription>
              </CardHeader>
              <CardContent>
                {project.description ? (
                  <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                    {project.description}
                  </p>
                ) : null}
                <dl className="grid grid-cols-3 gap-3 text-xs">
                  <Stat label="Runs" value={project.run_count} />
                  <Stat label="Active" value={project.active_run_count} />
                  <Stat label="Artifacts" value={project.artifact_count} />
                </dl>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div>
      <dt className="label-micro text-muted-foreground">{label}</dt>
      <dd className="font-display text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
